import * as React from 'react';
import axios from "axios";
import { useState, useEffect, useContext} from 'react';
import { AuthContext } from "../context/AuthContext";
import { CloudContext } from "../context/CloudContext";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from "react-i18next";
import config from '../config.js';
import { useSnackbar } from 'notistack';
import Slider from '@mui/material/Slider';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import {deleteDirectoryS3} from "../utils/interfaceS3";
import {deleteUserDataFromCesiumIon} from "../utils/cesiumManagement.js"
import {
  PLAN,
  PAID_DURATION_MONTHS,
  DEFAULT_PAID_DURATION_MONTHS,
  getPlan,
  isUnlimited,
  isAccessExpired,
  daysRemaining,
  computePaidExpiry,
  computeTrialExpiry,
} from '../utils/accessControl';

export default function UserSettingDialog({ setOpen, open, user, reloadUsers}) {

    const {  t } = useTranslation();
    const {idToken,jwt} = useContext(AuthContext);
     const {cesium_token, region, idpool} = useContext(CloudContext);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        setValue(user.space);
        setMaxUploads(user.maxUploadPerMonth);
    }, [user]);

    const [value, setValue] = useState(user.space);
    const [maxUploads, setMaxUploads] = useState(user.maxUploadPerMonth);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [paidMonths, setPaidMonths] = useState(DEFAULT_PAID_DURATION_MONTHS);
    const [extendWeeks, setExtendWeeks] = useState(4);
    const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);

    function valuetext(value) {
        return `${value}Go`;
      }

    const handleSave = () => {
        setOpen(false);
        const data = {
            space: value,
            maxUploadPerMonth: maxUploads
        };
        axios.put(`${config.apiUrl}/api/users/${user.id}`, data, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`,
            }
        })
        .then(res => {
            enqueueSnackbar(`${t("SuccessEditUser")}`, {variant:'success'} );
            reloadUsers();
        })
        .catch(err => {
            enqueueSnackbar(`${t("ErrorEditUser")}`, {variant:'error'} );
        });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (event, newValue, activeThumb) => {
          setValue(newValue);
      };

    const handleChangeMaxUploads = (event, newValue, activeThumb) => {
        setMaxUploads(newValue);
    };

    const handleOpenConfirm = () => {
        setConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        setConfirmOpen(false);
    };

    const handleConfirmDelete = async () => {
        setConfirmOpen(false);
        console.log('Deleting user : ', `${user.id}`);
    
        try{
            const dataForm = new FormData();
            dataForm.append('data', JSON.stringify({
                userID: `${user.username}`,
            }))
            await axios.post(config.userDeleteApiUrl, dataForm, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                }
            })
            console.log("user deleted from cognito")
            try{
                await deleteDirectoryS3(user.username,idToken, region, idpool);
                console.log("user data deleted from S3")
            }catch (error) {
                console.error("user data not deleted from S3. Maybe the user did not upload anything");
            }; 
            await deleteUserDataFromCesiumIon(user,cesium_token);
            console.log("user data deleted from Cesium ION")
            const apiUrl = `${config.apiUrl}/api/users/${user.id}`;
            await axios.delete(apiUrl, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                }
            });  
            console.log("user deleted from strapi")
            enqueueSnackbar(`${t('SuccessDeleteUser')}`, { variant: 'success' });
            reloadUsers();
            setOpen(false);
        }catch (error) {
            console.error(error);
            enqueueSnackbar(`${t('ErrorDeleteUser')}`, { variant: 'error' });
        };    
    };

    const marks = [
        {
          value: 10,
          label: '10 Go',
        },
        {
          value: 100,
          label: '100 Go',
        },
      ];

      const marksMaxUploads = [
        {
          value: 5,
          label: '5 Go',
        },
        {
          value: 100,
          label: '100 Go',
        },
      ];

    // --- access status (derived from the selected user) ---
    const plan = getPlan(user);
    const unlimited = isUnlimited(user);
    const expired = isAccessExpired(user);
    const remaining = daysRemaining(user);
    const planLabel =
        plan === PLAN.PAID ? t('PlanPaid')
        : plan === PLAN.TRIAL ? t('PlanTrial')
        : t('PlanUnlimited');

    // Shared PUT: patch only the access fields, then refresh the admin user list.
    const applyAccessUpdate = (patch) => {
        axios.put(`${config.apiUrl}/api/users/${user.id}`, patch, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`,
            },
        })
            .then(() => {
                enqueueSnackbar(`${t('AccessUpdatedSuccess')}`, { variant: 'success' });
                reloadUsers();
            })
            .catch(() => {
                enqueueSnackbar(`${t('AccessUpdatedError')}`, { variant: 'error' });
            });
    };

    const handleActivatePaid = () => {
        const now = new Date();
        applyAccessUpdate({
            plan: PLAN.PAID,
            planStartedAt: now.toISOString(),
            accessExpiresAt: computePaidExpiry(paidMonths, now), // extends from today
        });
    };

    const handleExtendTrial = () => {
        if (!Number.isInteger(extendWeeks) || extendWeeks < 1) {
            enqueueSnackbar(`${t('warningTrialWeeks')}`, { variant: 'warning' });
            return;
        }
        const now = new Date();
        applyAccessUpdate({
            plan: PLAN.TRIAL,
            planStartedAt: now.toISOString(),
            trialWeeks: extendWeeks,
            accessExpiresAt: computeTrialExpiry(extendWeeks, now), // extends from today
        });
    };

    const handleRevoke = () => {
        setConfirmRevokeOpen(false);
        // Set expiry to now => user is immediately expired at next login.
        applyAccessUpdate({ accessExpiresAt: new Date().toISOString() });
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth={true}>
                <Box sx={{ p: 3 }}>
                    <DialogTitle>
                        <Typography variant="h3" component={"div"}>
                        {t("UserSettings1")}
                        </Typography>
                    </DialogTitle>
                    <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: theme.palette.grey[500],
                    })}
                    >
                    <CancelIcon />
                    </IconButton>
                    <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <Typography variant="h5">
                        {t("UserSettings2")}
                        </Typography>
                        <Typography color="text.secondary" variant="body2" sx={{ pt: 1 }}>
                        {t("UserSettings3")}
                        </Typography>
                        <Box sx={{pr: 4 , pt: 2 }}>
                        <Slider
                            aria-label="Storage"
                            getAriaValueText={valuetext}
                            valueLabelDisplay="auto"
                            step={10}
                            marks={marks}
                            min={10}
                            max={100}
                            value={value}
                            onChange={handleChange}
                        />
                        </Box>
                        
                    </Box>
                    <Box sx={{ py: 2 }}>
                        <Typography variant="h5">
                        {t("UserSettings4")}
                        </Typography>
                        <Typography color="text.secondary" variant="body2" sx={{ pt: 1 }}>
                        {t("UserSettings5")}
                        </Typography>
                        <Box sx={{pr: 4 , pt: 2 }}>
                        <Slider
                            aria-label="Storage"
                            getAriaValueText={valuetext}
                            valueLabelDisplay="auto"
                            step={5}
                            min={5}
                            marks={marksMaxUploads}
                            max={100}
                            value={maxUploads}
                            onChange={handleChangeMaxUploads}
                        />
                        </Box>
                        
                    </Box>
                    <Box sx={{ py: 2 }}>
                        <Typography variant="h5">{t("AccessManagementTitle")}</Typography>

                        {/* current status */}
                        <Box sx={{ pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Typography variant="body2" color="text.secondary">{t("Plan")} :</Typography>
                                    <Chip size="small" label={planLabel} />
                                    {unlimited ? (
                                        <Chip size="small" color="info" variant="outlined" label={t("NeverExpires")} />
                                    ) : (
                                        <Chip
                                            size="small"
                                            color={expired ? 'error' : 'success'}
                                            label={expired ? t("AccessExpiredLabel") : t("AccessActive")}
                                        />
                                    )}
                                </Stack>

                                {!unlimited && (
                                    <Box sx={{ pt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("AccessExpiresOn")} : {new Date(user.accessExpiresAt).toLocaleDateString()}
                                            {remaining !== null && remaining > 0
                                                ? ` (${t("DaysRemainingValue", { count: remaining })})`
                                                : ''}
                                        </Typography>
                                        {user.planStartedAt && (
                                            <Typography variant="body2" color="text.secondary">
                                                {t("AccessStartedOn")} : {new Date(user.planStartedAt).toLocaleDateString()}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {!unlimited && !expired && (
                                <Button
                                    variant="text"
                                    color="error"
                                    onClick={() => setConfirmRevokeOpen(true)}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {t("RevokeAccess")}
                                </Button>
                            )}
                        </Box>

                        {/* activate / renew paid access */}
                        <Box sx={{ pt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ width: 160 }}>
                                <InputLabel id="paid-duration-label">{t("Duration")}</InputLabel>
                                <Select
                                    labelId="paid-duration-label"
                                    label={t("Duration")}
                                    value={paidMonths}
                                    onChange={(e) => setPaidMonths(e.target.value)}
                                >
                                    {PAID_DURATION_MONTHS.map((m) => (
                                        <MenuItem key={m} value={m}>{t("DurationMonths", { count: m })}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" color="primary" onClick={handleActivatePaid}>
                                {t("ActivatePaidAccess")}
                            </Button>
                        </Box>

                        {/* extend trial */}
                        <Box sx={{ pt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                label={t("TrialPeriodWeeks")}
                                type="number"
                                value={extendWeeks}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    setExtendWeeks(Number.isNaN(v) ? '' : v);
                                }}
                                slotProps={{ htmlInput: { min: 1, max: 104, step: 1 } }}
                                sx={{ width: 160 }}
                            />
                            <Button variant="outlined" color="primary" onClick={handleExtendTrial}>
                                {t("ExtendTrial")}
                            </Button>
                        </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Alert severity="error"><Typography variant="h5">{t("UserDeletionTitle")}</Typography>
                    <Box sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ flex: 4 }}>    
                                <Typography color="text.secondary" variant="body2" sx={{ pt: 0 }}>
                                {t("UserDeletionDescription")}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Button variant="contained" color="error" sx={{ textTransform: 'none' }} onClick={handleOpenConfirm}>
                                    <DeleteOutlineIcon/>
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={handleSave}>{t("Save")}</Button>
                </DialogActions>
            </Box>
            </Dialog>
            <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth='xs' fullWidth={true}>
                <Box sx={{ p: 3 }}>
                    <DialogTitle>
                        <Typography variant="h5" component={'div'}>
                        {t("ConfirmUserDeletionTitle")}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" variant="body2">
                        {t("ConfirmUserDeletionDescription")}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseConfirm}>
                            {t("Cancel")}
                        </Button>
                        <Button color="error" variant="contained" sx={{ textTransform: 'none' }} onClick={handleConfirmDelete}>
                            {t("DeleteUser")}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
            <Dialog open={confirmRevokeOpen} onClose={() => setConfirmRevokeOpen(false)} maxWidth='xs' fullWidth>
                <Box sx={{ p: 3 }}>
                    <DialogTitle>
                        <Typography variant="h5" component="div">{t("ConfirmRevokeTitle")}</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" variant="body2">{t("ConfirmRevokeMessage")}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmRevokeOpen(false)}>{t("Cancel")}</Button>
                        <Button color="error" variant="contained" onClick={handleRevoke}>{t("RevokeAccess")}</Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
}
